/**
 * T-A18：选图乐观预览 — 快筛、blob URL 登记、乐观入列项构造。
 */
import { format } from 'date-fns';
import { UPLOAD_CONFIG } from '@/utils/uploadConfig';

export function isOptimisticPreviewEnabled() {
  return UPLOAD_CONFIG.optimisticPreview === true;
}

export function fileQuickKey(file) {
  if (!file) return '';
  return `${file.name || ''}-${file.size || 0}-${file.lastModified || 0}`;
}

export function createOptimisticLocalId() {
  return `LIB-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * 同批 + 已有列表（name+size+lastModified）快筛。
 * @returns {{ accepted: File[], skippedQuick: number }}
 */
export function quickFilterUploadFiles(files, existingImages = []) {
  const list = Array.isArray(files) ? files : [];
  const existingKeys = new Set();
  for (const img of (Array.isArray(existingImages) ? existingImages : [])) {
    if (!img) continue;
    const name = img.name || img.fileName || '';
    const size = img.size != null ? img.size : '';
    const lastModified = img.lastModified != null ? img.lastModified : '';
    if (name !== '' && size !== '' && lastModified !== '') {
      existingKeys.add(`${name}-${size}-${lastModified}`);
    }
    // 已入列乐观卡也可能只有 name
    if (img._quickKey) existingKeys.add(String(img._quickKey));
  }

  const seen = new Set(existingKeys);
  const accepted = [];
  let skippedQuick = 0;
  for (const file of list) {
    const key = fileQuickKey(file);
    if (!key || seen.has(key)) {
      skippedQuick += 1;
      continue;
    }
    seen.add(key);
    accepted.push(file);
  }
  return { accepted, skippedQuick };
}

export function stampOptimisticLocalId(target, localId) {
  if (!target || !localId) return target;
  try {
    Object.defineProperty(target, '_optimisticLocalId', {
      value: String(localId),
      enumerable: false,
      configurable: true,
      writable: true,
    });
  } catch {
    // eslint-disable-next-line no-param-reassign
    target._optimisticLocalId = String(localId);
  }
  return target;
}

export function readOptimisticLocalId(file) {
  const id = file?._optimisticLocalId;
  return id != null && id !== '' ? String(id) : '';
}

/**
 * @param {File} file
 * @param {{
 *   id: string,
 *   blobUrl: string,
 *   uploadFolderId?: string,
 *   uploadOriginSource?: string,
 *   creatorPayload?: object,
 *   createdAt?: string,
 * }} meta
 */
function stripUploadDisplayName(name) {
  const raw = String(name || '').trim();
  if (!raw) return '';
  const base = raw.includes('/') ? raw.split('/').pop() : raw;
  const dot = base.lastIndexOf('.');
  if (dot > 0 && dot < base.length - 1) return base.slice(0, dot);
  return base;
}

export function buildOptimisticImageSpaceItem(file, meta) {
  const id = String(meta.id);
  const blobUrl = String(meta.blobUrl || '');
  const createdAt = meta.createdAt || format(new Date(), 'yyyy-MM-dd HH:mm');
  const rawName = file?.name || '';
  return {
    id,
    clientLocalId: id,
    name: stripUploadDisplayName(rawName) || rawName,
    url: '',
    dataUrl: blobUrl,
    src: blobUrl,
    ingestPending: true,
    createdAt,
    size: file?.size ?? null,
    source: meta.uploadOriginSource || 'upload',
    folderId: meta.uploadFolderId || '',
    usageCount: 0,
    useCount: 0,
    _quickKey: fileQuickKey(file),
    ...(meta.creatorPayload && typeof meta.creatorPayload === 'object' ? meta.creatorPayload : {}),
  };
}

/** blob URL 生命周期表：成功替换 / 内容撤卡时必须 revoke */
export class OptimisticBlobRegistry {
  constructor() {
    /** @type {Map<string, string>} */
    this._byId = new Map();
  }

  register(id, blobUrl) {
    const key = String(id || '');
    const url = String(blobUrl || '');
    if (!key || !url) return;
    const prev = this._byId.get(key);
    if (prev && prev !== url) {
      this._safeRevoke(prev);
    }
    this._byId.set(key, url);
  }

  revokeOne(id) {
    const key = String(id || '');
    if (!key || !this._byId.has(key)) return;
    const url = this._byId.get(key);
    this._byId.delete(key);
    this._safeRevoke(url);
  }

  revokeMany(ids) {
    for (const id of (Array.isArray(ids) ? ids : [])) {
      this.revokeOne(id);
    }
  }

  revokeAll() {
    for (const url of this._byId.values()) {
      this._safeRevoke(url);
    }
    this._byId.clear();
  }

  has(id) {
    return this._byId.has(String(id || ''));
  }

  size() {
    return this._byId.size;
  }

  ids() {
    return [...this._byId.keys()];
  }

  _safeRevoke(url) {
    if (!url || typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }
}
